/**
 * Persisted "My OHC has edits the server has not seen" marker.
 *
 * The user store writes every favourite/itinerary edit to localStorage at
 * once and pushes it to /api/user/data a second later. When that push failed
 * (one bar of signal reports navigator.onLine === true, so the edit is
 * allowed, but the request dies) nothing remembered it. On the next launch
 * loadFromServer() saw a returning device and REPLACED local data with the
 * server copy, silently discarding the edit.
 *
 * The marker survives a reload, so the next launch knows to push before it
 * loads. It records WHICH account made the edits: on a shared device the next
 * person to sign in must not have someone else's list pushed into theirs.
 * Edits made while signed out are never marked — they are local-only, and the
 * first-login merge in loadFromServer() already handles them.
 */

export interface PendingSync {
    userId: string;
    at: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function pendingSyncKey(year: number | string) {
    return `pendingSync${year}`;
}

export function readPendingSync(storage: StorageLike | undefined, key: string): PendingSync | null {
    if (!storage) return null;
    try {
        const raw = storage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed.userId === 'string' && parsed.userId ? parsed : null;
    } catch {
        return null;
    }
}

export function writePendingSync(storage: StorageLike | undefined, key: string, userId: string | null | undefined, now = Date.now()) {
    if (!storage || !userId) return;
    try {
        storage.setItem(key, JSON.stringify({ userId, at: now }));
    } catch {
        // Quota or private-mode failure: nothing better to do than carry on.
    }
}

export function clearPendingSync(storage: StorageLike | undefined, key: string) {
    if (!storage) return;
    try {
        storage.removeItem(key);
    } catch {}
}

/** True when there are unsynced edits that belong to the signed-in account. */
export function hasPendingFor(pending: PendingSync | null, userId: string | null | undefined): boolean {
    return !!pending && !!userId && pending.userId === userId;
}

// 5s, 15s, 30s, 1m, 2m, then every 5m. Short at first because the common
// failure is a dropped request on a weak signal that the next attempt gets
// through; capped so a phone left in a pocket all afternoon is not hammering
// the API.
const RETRY_DELAYS_MS = [5_000, 15_000, 30_000, 60_000, 120_000, 300_000];

export function retryDelayMs(attempt: number): number {
    const i = Math.max(0, Math.min(attempt, RETRY_DELAYS_MS.length - 1));
    return RETRY_DELAYS_MS[i]!;
}
